import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, Briefcase, AlertCircle, Check, Plus } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { gerarNomeOferta } from '../utils/ofertaHelpers';
import { DEAL_STAGES } from '../constants/dealStages';
import { UF_OPTIONS } from '../constants/brazilianStates';

/**
 * Quick-create modals — Sessão 4.7.
 * 
 * Princípio: "inline creation" — humano pode criar entidade SEM sair do contexto atual.
 * Usados dentro do EmailDetailDrawer para desbloquear fluxo de vinculação.
 * 
 * Campos mínimos para desbloquear. Refino completo é feito nas telas dedicadas.
 * 
 * Todos usam z-index [10003] para sobrepor:
 *   - modal da Inbox (z-10001)
 *   - drawer de detalhe (z-10002)
 * 
 * Cada modal recebe `onCreated(entidade)` — callback que retorna a entidade criada
 * para o pai preencher automaticamente o dropdown original.
 */

// ============================================================================
// Base wrapper — modal genérico de criação rápida
// ============================================================================
const QuickCreateWrapper = ({ title, icon: Icon, onClose, children, saving, onSave, canSave }) => (
  <div className="fixed inset-0 z-[10003] flex items-center justify-center p-4 pointer-events-none">
    <div 
      className="absolute inset-0 bg-black/40 pointer-events-auto"
      onClick={onClose}
    />
    <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-md pointer-events-auto animate-fade-in">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-green-100 p-1.5 rounded-lg">
            <Icon className="text-green-600" size={16} />
          </div>
          <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X size={18} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        {children}
      </div>
      <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-200 rounded"
        >
          Cancelar
        </button>
        <button
          onClick={onSave}
          disabled={saving || !canSave}
          className="px-4 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
        >
          <Check size={12} /> {saving ? 'Criando...' : 'Criar'}
        </button>
      </div>
    </div>
  </div>
);

// ============================================================================
// QuickCreateEmpresa — cadastro rápido de empresa
// ============================================================================
export const QuickCreateEmpresa = ({ onClose, onCreated, nomeInicial = '' }) => {
  const [nome, setNome] = useState(nomeInicial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const nomeUpper = nome.trim().toUpperCase();
      const { data, error: err } = await supabase
        .from('empresas')
        .insert({
          empresa: nomeUpper,
          company_name: nome.trim(),
        })
        .select()
        .single();
      
      if (err) throw err;
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickCreateWrapper
      title="Nova Empresa"
      icon={Building2}
      onClose={onClose}
      saving={saving}
      onSave={handleSave}
      canSave={!!nome.trim()}
    >
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Nome da empresa
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) handleSave(); }}
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Ex: FINVEST"
          autoFocus
        />
        <div className="text-[10px] text-slate-500 mt-1">
          Dica: refine o cadastro depois na tela "Nova Empresa" do header.
        </div>
      </div>
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
          <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-800">{error}</span>
        </div>
      )}
    </QuickCreateWrapper>
  );
};

// ============================================================================
// QuickCreateProprietaria — cadastro de empresa proprietária (vendedor)
// Funcionalmente igual a empresa, mas deixa claro o papel no UI
// ============================================================================
export const QuickCreateProprietaria = ({ onClose, onCreated, nomeInicial = '' }) => {
  const [nome, setNome] = useState(nomeInicial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const nomeUpper = nome.trim().toUpperCase();
      const { data, error: err } = await supabase
        .from('empresas')
        .insert({
          empresa: nomeUpper,
          company_name: nome.trim(),
          segmento: 'Proprietário de área',
        })
        .select()
        .single();
      
      if (err) throw err;
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickCreateWrapper
      title="Novo Proprietário"
      icon={Building2}
      onClose={onClose}
      saving={saving}
      onSave={handleSave}
      canSave={!!nome.trim()}
    >
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Nome da empresa proprietária (vendedor)
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) handleSave(); }}
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Ex: FUNSAI, ou nome da família"
          autoFocus
        />
        <div className="text-[10px] text-slate-500 mt-1">
          Quem é o dono do terreno na oferta? Dica: pode ser holding familiar, fundação ou pessoa física.
        </div>
      </div>
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
          <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-800">{error}</span>
        </div>
      )}
    </QuickCreateWrapper>
  );
};

// ============================================================================
// QuickCreateArea — cadastro mínimo de área
// ============================================================================
export const QuickCreateArea = ({ onClose, onCreated, nomeInicial = '' }) => {
  const [nome, setNome] = useState(nomeInicial);
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState(''); // inicia VAZIO — sem default São Paulo
  const [uf, setUf] = useState('');         // inicia VAZIO — sem default SP
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canSave = !!nome.trim() && !!cidade.trim() && !!uf;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('areas')
        .insert({
          nome: nome.trim(),
          localizacao_bairro: bairro.trim() || null,
          localizacao_cidade: cidade.trim(),
          localizacao_uf: uf,
          status: 'em_prospeccao',
        })
        .select()
        .single();

      if (err) throw err;
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickCreateWrapper
      title="Nova Área"
      icon={MapPin}
      onClose={onClose}
      saving={saving}
      onSave={handleSave}
      canSave={canSave}
    >
      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Nome/código da área
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Ex: 083.103 Ruth Cardoso 6.000m²"
          autoFocus
        />
      </div>
      <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1">
          <MapPin size={11} className="text-green-600" /> Localização
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 block mb-1">
            Bairro (opcional)
          </label>
          <input
            type="text"
            value={bairro}
            onChange={(e) => setBairro(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Ex: Pinheiros, Vila Carioca"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Cidade <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={cidade}
              onChange={(e) => setCidade(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Ex: São Paulo"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              UF <span className="text-red-500">*</span>
            </label>
            <select
              value={uf}
              onChange={(e) => setUf(e.target.value)}
              className="w-full px-2 py-2 border border-slate-300 rounded text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Selecione...</option>
              {UF_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>
        <div className="text-[10px] text-slate-500">
          Campos detalhados (m², zoneamento, palavras-chave) refine depois na tela "Áreas".
        </div>
      </div>
      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
          <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-800">{error}</span>
        </div>
      )}
    </QuickCreateWrapper>
  );
};

// ============================================================================
// QuickCreateOferta — cadastro de oferta com nome auto-gerado EMPRESA: ÁREA
// ============================================================================
export const QuickCreateOferta = ({ onClose, onCreated, empresa, area }) => {
  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-gera nome quando empresa+área estão definidas
  useEffect(() => {
    setNome(gerarNomeOferta(empresa, area));
  }, [empresa, area]);

  const handleSave = async () => {
    if (!nome.trim() || !empresa?.id || !area?.id) return;
    setSaving(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('ofertas')
        .insert({
          nome: nome.trim(),
          empresa_id: empresa.id,
          area_id: area.id,
          status: DEAL_STAGES.PROSPECTAR,  // estado inicial padrão
        })
        .select()
        .single();
      
      if (err) throw err;
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickCreateWrapper
      title="Nova Oferta"
      icon={Briefcase}
      onClose={onClose}
      saving={saving}
      onSave={handleSave}
      canSave={!!nome.trim() && !!empresa?.id && !!area?.id}
    >
      <div className="bg-indigo-50 border border-indigo-200 rounded p-2 space-y-1 text-xs">
        <div className="flex items-center gap-1.5">
          <Building2 size={10} className="text-indigo-600" />
          <span className="text-slate-600">Empresa:</span>
          <strong className="text-indigo-900">{empresa?.empresa || '— não definida —'}</strong>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin size={10} className="text-indigo-600" />
          <span className="text-slate-600">Área:</span>
          <strong className="text-indigo-900">{area?.nome || '— não definida —'}</strong>
        </div>
      </div>

      {(!empresa?.id || !area?.id) && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 flex items-start gap-1.5">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
          <span>Preencha Empresa e Área no drawer antes de criar oferta.</span>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-slate-700 block mb-1">
          Nome da oferta (auto-gerado, editável)
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim() && empresa?.id && area?.id) handleSave(); }}
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          placeholder="Ex: CONX: Gama Lobo"
        />
        <div className="text-[10px] text-slate-500 mt-1">
          Padrão do sistema: <strong>EMPRESA: ÁREA</strong>. Pode editar livremente se precisar.
        </div>
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
          <AlertCircle size={12} className="text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-red-800">{error}</span>
        </div>
      )}
    </QuickCreateWrapper>
  );
};

// ============================================================================
// Helper visual: label "+ Criar nova..." para incluir no topo de dropdowns
// ============================================================================
export const CRIAR_NOVO_OPTION_VALUE = '__CREATE_NEW__';

/**
 * Componente de dropdown customizado que inclui opção "+ Criar nova..." no topo.
 * Retorna `{value, onChange, onCreateNew}` nas props.
 */
export const SelectComCriar = ({ 
  label, 
  icon: Icon, 
  value, 
  onChange, 
  onCreateNew, 
  options, 
  valueKey = 'id', 
  labelKey = 'nome', 
  placeholder, 
  helperText,
  createLabel = '+ Criar nova...',
}) => {
  const handleChange = (e) => {
    const val = e.target.value;
    if (val === CRIAR_NOVO_OPTION_VALUE) {
      onCreateNew();
      return;
    }
    onChange(val);
  };

  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-700 flex items-center gap-1 mb-1">
        {Icon && <Icon size={11} />} {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={handleChange}
          className="w-full text-xs px-2 py-1.5 pr-6 border border-slate-300 rounded bg-white appearance-none cursor-pointer hover:border-slate-400"
        >
          <option value="">{placeholder}</option>
          <option value={CRIAR_NOVO_OPTION_VALUE} style={{ color: '#16a34a', fontWeight: '600' }}>
            {createLabel}
          </option>
          {options.map(opt => (
            <option key={opt[valueKey]} value={opt[valueKey]}>
              {opt[labelKey]}
            </option>
          ))}
        </select>
        <Plus size={12} className="absolute right-2 top-2 text-slate-400 pointer-events-none" />
      </div>
      {helperText && (
        <div className="text-[10px] text-slate-500 mt-0.5">{helperText}</div>
      )}
    </div>
  );
};
